import axios, { AxiosResponse } from 'axios';
import { AppThunk } from '../../app/store';
import {
  DOWNLOAD_CHAT_LOG_CSV_FLOW_ERR_MSG,
  DOWNLOAD_CLIENTS_LOG_CSV_FLOW_ERR_MSG,
  GET_FILE_TO_LIVE_COUNT_API_ERR_MSG,
  NOT_PARAMETER_ERR_MSG,
} from '../../utils/Message';
import { setError, setOutputCSVStatus } from '../../features/uiSlice';
import { UI_STATUS } from '../../utils/Const';
import { GetLiveConnectionArch } from '../../types/API';

type getCountType = {
  cid: number;
  flpid: number;
  timeInterval?: number;
  from?: number;
  to?: number;
};

/** 疑似ライブ同時接続取得 */
export const downloadFileToLiveClientsLogCSVFlow =
  (arg: getCountType): AppThunk =>
  async (dispatch) => {
    // 処理中をセット
    dispatch(setOutputCSVStatus(UI_STATUS.PROCESSING));
    try {
      // パラメータに不足がある場合はエラー
      if (!arg.from || !arg.to) {
        const noparam: string[] = [];
        if (!arg.from) {
          noparam.push('ライブ開始時刻');
        }
        if (!arg.to) {
          noparam.push('ライブ停止時刻');
        }
        dispatch(
          setError({
            msg: GET_FILE_TO_LIVE_COUNT_API_ERR_MSG,
            detail: `${NOT_PARAMETER_ERR_MSG}: ${noparam.join()}`,
          })
        );
        return true;
      }
      // ライブ同時接続取得API実行
      const response: AxiosResponse<GetLiveConnectionArch> = await axios.get(
        `${String(process.env.REACT_APP_BACKEND_API_ENDPOINT)}getCount`,
        {
          params: {
            cid: arg.cid,
            lpid: arg.flpid,
            leid: 999999,
            timeInterval: arg.timeInterval || 30,
            from: arg.from,
            to: arg.to,
          },
          headers: {
            accept: 'application/json',
            'Content-type': 'application/json',
          },
        }
      );
      // 正常終了
      const header = `${Object.keys(response.data.data[0]).join(',')}\r\n`;
      const data = `${response.data.data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((record: any) => Object.values(record).join(','))
        .join('\r\n')}\r\n\r\n`;
      const footer = `${['累計視聴者数', response.data.total ?? 0].join(
        ','
      )}\r\n`;
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      const url = URL.createObjectURL(
        new Blob([bom, header, data, footer], { type: 'text/csv' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${arg.cid}_${arg.flpid}_${arg.from}_${arg.to}.csv`
      );
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // 404エラーは空のCSVファイルを出力させる
      if (error.response?.data?.statusCode === 404) {
        const url = URL.createObjectURL(new Blob([], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${arg.cid}_${arg.flpid}_${arg.from}_${arg.to}.csv`
        );
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
        link.parentNode?.removeChild(link);
        return true;
      }
      // 異常終了
      return dispatch(
        setError({
          msg: DOWNLOAD_CLIENTS_LOG_CSV_FLOW_ERR_MSG,
          detail: JSON.stringify(error.response?.data || error.message),
        })
      );
    } finally {
      // 完了をセット
      dispatch(setOutputCSVStatus(UI_STATUS.COMPLETED));
    }
  };

type getChatType = {
  readAPIToken: string;
  flpid: number;
  setNotFoundChatlogDialog: React.Dispatch<React.SetStateAction<boolean>>;
};

/** 疑似ライブチャット投稿履歴取得 */
export const downloadFileToLiveChatLogCSVFlow =
  (arg: getChatType): AppThunk =>
  async (dispatch) => {
    // 処理中をセット
    dispatch(setOutputCSVStatus(UI_STATUS.PROCESSING));
    try {
      // 疑似ライブチャット投稿履歴取得API実行
      const response: AxiosResponse<string> = await axios.get(
        `${String(
          process.env.REACT_APP_BACKEND_API_ENDPOINT
        )}filetolive/getFileToLiveChatMsgArch`,
        {
          params: {
            t: arg.readAPIToken,
            flpid: arg.flpid,
            type: 'text',
          },
          headers: {
            accept: 'application/json',
            'Content-type': 'application/json',
          },
        }
      );
      // 正常終了
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      const url = URL.createObjectURL(
        new Blob([bom, response.data], { type: 'text/csv' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chatlog_${arg.flpid}.csv`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
      return true;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // 404エラーはダイアログを表示
      if (error.response?.data?.statusCode === 404) {
        arg.setNotFoundChatlogDialog(true);
        return true;
      }
      // 異常終了
      return dispatch(
        setError({
          msg: DOWNLOAD_CHAT_LOG_CSV_FLOW_ERR_MSG,
          detail: JSON.stringify(error.response?.data || error.message),
        })
      );
    } finally {
      // 完了をセット
      dispatch(setOutputCSVStatus(UI_STATUS.COMPLETED));
    }
  };
